import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Envelope, CheckCircle, ArrowRight } from 'phosphor-react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { clearError, requestMagicLink, resetMagicLinkSent } from '../redux/slices/authSlice';
import AmbientBackground from '../components/AmbientBackground';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isLoading, error, isAuthenticated, magicLinkSent } = useAppSelector((state) => state.auth);

  useEffect(() => {
    dispatch(clearError());
    dispatch(resetMagicLinkSent());
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!email) return;
    await dispatch(requestMagicLink({ email })).unwrap();
  };

  return (
    <div className="relative min-h-screen px-4 py-10 text-white sm:px-6 lg:px-8">
      <AmbientBackground />

      <div className="relative mx-auto flex min-h-[calc(100vh-5rem)] max-w-lg items-center justify-center">
        <div className="w-full gap-8">
          <section className="rounded-[2rem] border border-white/10 bg-white/10 p-6 shadow-glow backdrop-blur-2xl sm:p-8">
            <div className="mb-8 flex items-center justify-center">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-brand-500/30 blur-2xl" />
                <div className="relative flex h-20 w-20 items-center justify-center rounded-[1.75rem] border border-brand-400/30 bg-brand-500/10">
                  <Envelope size={36} weight="duotone" className="text-brand-200" />
                </div>
              </div>
            </div>

            {magicLinkSent ? (
              <div className="space-y-4 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand-500/15 text-brand-200 ring-1 ring-brand-400/25">
                  <CheckCircle size={30} weight="fill" />
                </div>
                <h2 className="text-2xl font-semibold">Check your email</h2>
                <p className="text-sm leading-6 text-white/75">
                  We sent a magic link to <span className="font-medium text-white">{email}</span>.
                </p>
                <button
                  type="button"
                  onClick={() => dispatch(resetMagicLinkSent())}
                  className="inline-flex items-center gap-2 rounded-full bg-brand-500 px-5 py-3 text-sm font-medium text-white transition hover:bg-brand-400"
                >
                  Send another link <ArrowRight size={16} />
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="email" className="mb-2 block text-sm font-medium text-white/90">
                    Email address
                  </label>
                  <div className="relative">
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder="you@veraconnect.com"
                      disabled={isLoading}
                      className="w-full rounded-2xl border border-white/10 bg-black/25 px-4 py-3 pl-11 text-white outline-none transition placeholder:text-white/35 focus:border-brand-400/60 focus:ring-2 focus:ring-brand-400/20 disabled:opacity-60"
                    />
                    <Envelope size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/45" />
                  </div>
                </div>

                {error ? (
                  <div className="rounded-2xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-100">
                    {error}
                  </div>
                ) : null}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-brand-500 px-5 py-3.5 text-sm font-semibold text-white transition hover:bg-brand-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isLoading ? 'Sending magic link...' : 'Send login link'}
                </button>
              </form>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
